import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';

import type { ImageInAtom } from '@/features/image-rendering/atoms/imageFiles';
import {
  selectedImageAtom,
  selectedPreviewImageAtom,
  uploadedImagesAtom,
} from '@/features/image-rendering/atoms/imageFiles';
import type { RenderingImage } from '@/features/renderings/shared/atoms/image-files';
import {
  renderingsSelectedImageAtom,
  renderingsSelectedPreviewImageAtom,
  renderingsUploadedImagesAtom,
} from '@/features/renderings/shared/atoms/image-files';

const getStableKey = (img: RenderingImage) => img.originalImageId ?? img.id;

const toLegacyImage = (img: RenderingImage): ImageInAtom => ({
  id: img.id,
  originalImage: img.originalImage,
  originalImageId: img.originalImageId,
  renderedImage: img.renderedImage,
  fileType: img.fileType,
  file: img.file,
});

export const useSyncSharedToLegacyImageRendering = () => {
  const sharedUploaded = useAtomValue(renderingsUploadedImagesAtom);
  const sharedSelected = useAtomValue(renderingsSelectedImageAtom);
  const sharedSelectedPreview = useAtomValue(renderingsSelectedPreviewImageAtom);

  const setLegacyUploaded = useSetAtom(uploadedImagesAtom);
  const setLegacySelected = useSetAtom(selectedImageAtom);
  const setLegacySelectedPreview = useSetAtom(selectedPreviewImageAtom);

  const lastSigRef = useRef('');

  useEffect(() => {
    const seen = new Set<string>();
    const mappedUploaded: ImageInAtom[] = [];

    for (const img of sharedUploaded) {
      const key = getStableKey(img);
      if (seen.has(key)) continue;
      seen.add(key);
      mappedUploaded.push(toLegacyImage(img));
    }

    const sig = JSON.stringify({
      list: mappedUploaded.map((i) => i.originalImageId ?? i.id),
      selected: sharedSelected ? getStableKey(sharedSelected) : null,
      preview: sharedSelectedPreview ? getStableKey(sharedSelectedPreview) : null,
    });

    if (lastSigRef.current === sig) return;
    lastSigRef.current = sig;

    setLegacyUploaded(mappedUploaded);
    // Legacy atoms are not optional in types, but the legacy page clears them on unmount.
    setLegacySelected(
      sharedSelected ? toLegacyImage(sharedSelected) : (undefined as unknown as ImageInAtom)
    );
    setLegacySelectedPreview(
      sharedSelectedPreview
        ? toLegacyImage(sharedSelectedPreview)
        : (undefined as unknown as ImageInAtom)
    );
  }, [
    setLegacySelected,
    setLegacySelectedPreview,
    setLegacyUploaded,
    sharedSelected,
    sharedSelectedPreview,
    sharedUploaded,
  ]);
};
